/**
 * Warms the code chunk for an internal route before the visitor clicks.
 *
 * Prefetch only fires on hover / focus / touch-start of an internal link, is
 * capped per session, and is skipped entirely on Save-Data or slow
 * connections, so it never costs meaningful bandwidth.
 */

type Loader = () => Promise<unknown>;

/**
 * Loaders use the exact same specifiers as the lazy() calls in AnimatedRoutes,
 * so a prefetch warms the very chunk the route will use — no duplicated code.
 */
const staticLoaders: Record<string, Loader> = {
  "/about": () => import("@/pages/About"),
  "/services": () => import("@/pages/Services"),
  "/services/fractional-cfo-finance-director": () => import("@/pages/services/FractionalCFO"),
  "/blog": () => import("@/pages/Blog"),
  "/resources": () => import("@/pages/Resources"),
  "/contact": () => import("@/pages/Contact"),
  "/industries": () => import("@/pages/Industries"),
  "/faq": () => import("@/pages/FAQ"),
  "/calculators": () => import("@/pages/Calculators"),
  "/hr-services": () => import("@/pages/HRServices"),
  "/areas": () => import("@/pages/areas/AreasIndex"),
  "/areas/new-forest": () => import("@/pages/areas/NewForest"),
  "/areas/salisbury": () => import("@/pages/areas/Salisbury"),
  "/areas/wimborne": () => import("@/pages/areas/WimborneBournemouth"),
  "/privacy-policy": () => import("@/pages/PrivacyPolicy"),
  "/privacy-rights": () => import("@/pages/PrivacyRights"),
  "/terms-of-service": () => import("@/pages/TermsOfService"),
  "/mtd-itsa": () => import("@/pages/mtd/MTDHub"),
  "/mtd-itsa/landlords": () => import("@/pages/mtd/MTDLandlords"),
  "/mtd-itsa/self-employed": () => import("@/pages/mtd/MTDSelfEmployed"),
  "/mtd-itsa/software-checklist": () => import("@/pages/mtd/MTDSoftware"),
};

const patternLoaders: { test: RegExp; load: Loader }[] = [
  { test: /^\/blog\/[^/]+$/, load: () => import("@/pages/BlogPost") },
  { test: /^\/services\/[^/]+$/, load: () => import("@/pages/services/ServicePage") },
  { test: /^\/resources\/[^/]+$/, load: () => import("@/pages/Resources") },
  { test: /^\/areas\/[^/]+$/, load: () => import("@/pages/areas/TownPage") },
];

const MAX_PREFETCHES = 12;
const done = new Set<string>();

function connectionAllowsPrefetch() {
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return !["slow-2g", "2g"].includes(conn.effectiveType ?? "");
}

export function prefetchRoute(pathname: string) {
  if (done.size >= MAX_PREFETCHES || done.has(pathname)) return;
  if (!connectionAllowsPrefetch()) return;

  const loader =
    staticLoaders[pathname] ?? patternLoaders.find((p) => p.test.test(pathname))?.load;
  if (!loader) return;

  done.add(pathname);
  void loader().catch(() => done.delete(pathname));
}

/**
 * Delegated listener — covers every internal link on the site without
 * touching a single component or adding markup.
 */
export function initLinkPrefetch() {
  if (typeof window === "undefined") return () => {};

  let timer: number | undefined;

  const consider = (target: EventTarget | null, delay: number) => {
    const anchor = (target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (!href.startsWith("/") || href.startsWith("//")) return;
    if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
    const path = href.split(/[?#]/)[0];
    if (path.startsWith("/admin")) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => prefetchRoute(path), delay);
  };

  const onOver = (e: Event) => consider(e.target, 80);
  const onFocus = (e: Event) => consider(e.target, 0);
  const onTouch = (e: Event) => consider(e.target, 0);
  const onOut = () => window.clearTimeout(timer);

  document.addEventListener("mouseover", onOver, { passive: true });
  document.addEventListener("mouseout", onOut, { passive: true });
  document.addEventListener("focusin", onFocus, { passive: true });
  document.addEventListener("touchstart", onTouch, { passive: true });

  return () => {
    window.clearTimeout(timer);
    document.removeEventListener("mouseover", onOver);
    document.removeEventListener("mouseout", onOut);
    document.removeEventListener("focusin", onFocus);
    document.removeEventListener("touchstart", onTouch);
  };
}
